import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'
import MaldivesDirectSearchFlightCalendarItemMobile from './MaldivesDirectSearchFlightCalendarItemMobile'
import MaldivesOfferCalendarItemMobile from './MaldivesOfferCalendarItemMobile'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlights } from 'actions/FlightActions'
import { fetchOfferList } from 'actions/OfferActions'
import getOfferListKey from 'lib/offer/offerListKey'
import { getFlightSearchKey } from 'lib/flights/flightUtils'
import { buildFlightSearchPayload, buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { HOTEL_OFFERS_LIMIT, MALDIVES_CHARTER_CONFIG } from 'constants/charterFlight'
import Places from 'constants/places'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FlightsClassTypes } from 'constants/flight'
import CurrencyContext from 'contexts/currencyContext'
import { CalendarItemBundleData } from '../hooks/types'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import { getLowestAvailableHotelPrice } from '../hooks/utils'

// Types
interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
}

interface Props {
  onDateSelect: (option: TripOption) => void
  selectedOptionId?: string
  nights: number
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  /** Ready-to-use calendar item data - no calculation needed **/
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  mode: 'search' | 'offer'
  /** Optional header actions to render at the top */
  headerActions?: React.ReactNode
  /** Optional scroll container ref for precise programmatic scrolling */
  scrollContainerRef?: React.RefObject<HTMLElement | null>
}

enum SelectionStep {
  MONTH = 'month',
  DATE = 'date',
}

const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-top: ${rem(12)};
  column-gap: ${rem(8)};
  row-gap: ${rem(12)};
`

const MonthCard = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};
  min-height: ${rem(70)};
  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }

  &.cheapest {
    border-color: ${props => props.theme.palette.highlight.secondary.normalBackground};
    border-width: 2px;
  }
`

const DateCardContainer = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.default.six};
  margin: 0 -${rem(20)};
  border-radius: ${rem(8)};
`
const EmptyStateGroup = styled(Group)`
  padding: ${rem(32)};
`

const CheapestBadge = styled.div`
  display: inline-block;
  padding: ${rem(4)} ${rem(8)};
  background-color: ${props => props.theme.palette.highlight.secondary.normalBackground};
  border-radius: ${props => props.theme.borderRadius.S};
  margin-left: ${rem(8)};
`

const MonthSection = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.default.six};
  margin: 0 -${rem(20)};
  padding: 0 ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.S};
  
  ${mediaQueryUp.tablet} {
    margin: 0 -${rem(24)};
  }
`

const HeaderActionsRow = styled(HorizontalContainerWithFadedOverflow)`
  margin-bottom: ${rem(12)};
`

const MonthHeaderGroup = styled(Group)`
  padding: ${rem(12)} 0;
`

const ClickableYearHeading = styled(Heading)`
  cursor: pointer;
  user-select: none;
  text-decoration: underline;
`

// Main Component
function MaldivesDirectSearchFlightCalendarMobile(props: Props) {
  const {
    onDateSelect,
    selectedOptionId,
    nights,
    occupancies,
    fareClass,
    priceMode,
    luxPlusEnabled,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    mode,
    itemBundleDataByDate,
    headerActions,
    scrollContainerRef,
  } = props

  const [currentStep, setCurrentStep] = useState<SelectionStep>(SelectionStep.MONTH)
  const [targetMonthSectionKey, setTargetMonthSectionKey] = useState<string>()
  const monthSectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const rooms = useMemo(() => occupancies || [{ adults: 2, children: 0 }], [occupancies])

  // Calculate total passengers for per-person pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(rooms),
    [rooms]
  )

  const currencyContext = useContext(CurrencyContext)
  const dispatch = useAppDispatch()
  // Get Redux state for all offers and flights
  const allOfferLists = useAppSelector(state => state.offer.offerLists)
  const allOffers = useAppSelector(state => state.offer.offers)
  const allFlights = useAppSelector(state => state.flights.searchV2Flights)
  const allOfferMetaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)

  // Prefetch data for all date ranges to enable month price calculation in search mode
  React.useEffect(() => {
    if (mode !== 'search') return

    Object.values(dateRangesByMonth).flat().forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
      // Load hotel offers
      const hotelFilters = buildBundleOfferFilters({
        destinationId: Places.Maldives.id,
        destinationName: Places.Maldives.name,
        checkIn: checkIn,
        checkOut: checkOut,
        rooms: rooms,
        limit: HOTEL_OFFERS_LIMIT,
      })
      dispatch(fetchOfferList(hotelFilters, { clientCheckAvailability: true }))

      // Load flight data
      const flightParams = buildFlightSearchPayload({
        departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
        arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
        departureDate: departureDate,
        returnDate: returnDate,
        occupancies: rooms,
        fareClass: fareClass,
        currency: currencyContext || 'AUD',
      })
      dispatch(fetchFlights(flightParams))
    })
  }, [dispatch, dateRangesByMonth, rooms, fareClass, priceMode, currencyContext, mode])

  // Generate months grouped by year with pricing from Redux
  const monthsByYear = useMemo(() => {
    const yearGroups: Record<number, Array<{
      name: string
      price: number
      fetching: boolean
      monthLabel: string
      year: number
    }>> = {}

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      let lowestPrice = 0
      let isMonthFetching = false

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        if (mode === 'search') {
          // Search mode: Get data from Redux (loaded by CalendarItems)
          // Use checkIn/checkOut (inner dates) for hotel queries
          const hotelFilters = buildBundleOfferFilters({
            destinationId: Places.Maldives.id,
            destinationName: Places.Maldives.name,
            checkIn: checkIn,
            checkOut: checkOut,
            rooms: rooms,
            limit: HOTEL_OFFERS_LIMIT,
          })

          // Use departureDate/returnDate (outer dates) for flight queries
          const flightParams = buildFlightSearchPayload({
            departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
            arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
            departureDate: departureDate,
            returnDate: returnDate,
            occupancies: rooms,
            fareClass: fareClass,
            currency: currencyContext || 'AUD'
          })

          const hotelKey = getOfferListKey(hotelFilters)
          const flightKey = getFlightSearchKey(flightParams)

          const offerList = allOfferLists[hotelKey]
          const flightData = allFlights[flightKey]

          // Check if still fetching
          if (offerList?.fetching || flightData?.fetching) {
            isMonthFetching = true
          }

          // Calculate hotel price - only from available offers with valid capacity
          const offerIds = offerList?.offerIds || []
          const offerMetaDataForList = allOfferMetaData[hotelKey]

          const lowestHotelPrice = getLowestAvailableHotelPrice({
            offerIds,
            allOffers,
            offerMetaData: offerMetaDataForList,
            rooms,
            nights,
            luxPlusEnabled,
          })

          // Calculate flight price
          const departingFlight = flightData?.fares?.[0]?.[0]
          const returningFlight = flightData?.fares?.[1]?.[0]
          const departingFare = departingFlight?.price?.all?.totalFare ?? 0
          const returningFare = returningFlight?.price?.all?.totalFare ?? 0
          const totalFlightFare = departingFare + returningFare

          // Calculate bundle price based on price mode
          if (lowestHotelPrice > 0 && totalFlightFare > 0 && !offerList?.fetching && !flightData?.fetching) {
            const bundleTotal = lowestHotelPrice + totalFlightFare
            const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
              ? bundleTotal / totalPassengers
              : bundleTotal

            if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
              lowestPrice = bundlePrice
            }
          }
        } else {
          // Offer mode: Get pre-calculated bundle data
          const itemData = itemBundleDataByDate?.[checkIn]

          // Check if still fetching
          if (itemData?.fetching) {
            isMonthFetching = true
          }

          // Calculate bundle price based on priceMode and luxPlusEnabled
          const bundlePrice = priceMode === 'total'
            ? (luxPlusEnabled ? (itemData?.memberTotalPrice || itemData?.totalPrice) : itemData?.totalPrice)
            : (luxPlusEnabled ? (itemData?.memberPricePerPerson || itemData?.pricePerPerson) : itemData?.pricePerPerson)

          if (itemData?.available && bundlePrice && bundlePrice > 0) {
            if (lowestPrice === 0 || bundlePrice < lowestPrice) {
              lowestPrice = bundlePrice
            }
          }
        }
      })

      const firstDate = dateRanges[0]?.checkIn
      const date = moment(firstDate)
      const year = date.year()

      if (!yearGroups[year]) {
        yearGroups[year] = []
      }

      yearGroups[year].push({
        name: monthName,
        price: lowestPrice || 0,
        fetching: isMonthFetching,
        monthLabel: date.format('MMM'),
        year: year,
      })
    })

    // Sort months within each year
    Object.keys(yearGroups).forEach(year => {
      yearGroups[parseInt(year)].sort((a, b) => moment(a.name).month() - moment(b.name).month())
    })

    return yearGroups
  }, [dateRangesByMonth, allOfferLists, allOffers, allFlights, allOfferMetaData, luxPlusEnabled, nights, rooms, fareClass, priceMode, totalPassengers, currencyContext, mode, itemBundleDataByDate])

  // Get all months for cheapest price calculation
  const allMonths = useMemo(() => {
    return Object.values(monthsByYear).flat()
  }, [monthsByYear])

  // Find the cheapest month
  const cheapestMonthPrice = useMemo(() => {
    const prices = allMonths.filter(m => m.price > 0).map(m => m.price)
    return prices.length > 0 ? Math.min(...prices) : 0
  }, [allMonths])

  // Generate trip options for all months (grouped by month for rendering)
  const allMonthOptions = useMemo(() => {
    const allOptions: Array<{
      id: string
      checkIn: string
      checkOut: string
      departDate: string
      returnDate: string
      month: string
    }> = []

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        allOptions.push({
          id: `${checkIn}-${nights}n`,
          checkIn,
          checkOut,
          departDate: departureDate,
          returnDate: returnDate,
          month: monthName,
        })
      })
    })

    return allOptions
  }, [dateRangesByMonth, nights])

  // Group options by month for rendering
  const optionsByMonth = useMemo(() => {
    const groups: Record<string, Array<{
      id: string
      checkIn: string
      checkOut: string
      departDate: string
      returnDate: string
      month: string
    }>> = {}

    allMonthOptions.forEach((option) => {
      const monthKey = moment(option.checkIn).format('MMMM YYYY')
      if (!groups[monthKey]) {
        groups[monthKey] = []
      }
      groups[monthKey].push(option)
    })

    // Sort months chronologically
    const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
      return moment(a, 'MMMM YYYY').valueOf() - moment(b, 'MMMM YYYY').valueOf()
    })

    return Object.fromEntries(sortedEntries)
  }, [allMonthOptions])

  // Handlers
  const handleMonthSelect = useCallback((monthName: string, monthKey: string) => {
    onMonthChange(monthName)
    setTargetMonthSectionKey(monthKey)
    setCurrentStep(SelectionStep.DATE)
  }, [onMonthChange])

  const handleBackToMonthSelect = useCallback(() => {
    setCurrentStep(SelectionStep.MONTH)
  }, [])

  const scrollContainerEl = scrollContainerRef?.current

  useLayoutEffect(() => {
    if (currentStep !== SelectionStep.DATE) {
      return
    }

    const resolvedKey = targetMonthSectionKey ?? Object.entries(optionsByMonth).find(([, options]) => {
      const optionMonth = options[0]?.month
      return optionMonth?.toLowerCase() === selectedMonth.toLowerCase()
    })?.[0] ?? Object.keys(optionsByMonth)[0]

    if (!resolvedKey) {
      return
    }

    const monthElement = monthSectionRefs.current[resolvedKey]
    if (!monthElement) {
      return
    }

    if (scrollContainerEl) {
      const containerRect = scrollContainerEl.getBoundingClientRect()
      const monthRect = monthElement.getBoundingClientRect()
      const currentScrollTop = scrollContainerEl.scrollTop ?? 0
      const offset = monthRect.top - containerRect.top + currentScrollTop - 16
      scrollContainerEl.scrollTo({
        top: Math.max(offset, 0),
        behavior: 'auto',
      })
    } else {
      monthElement.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      })
    }

    setTargetMonthSectionKey(undefined)
  }, [currentStep, targetMonthSectionKey, optionsByMonth, selectedMonth, scrollContainerEl])

  const renderHeader = (headerActions: React.ReactNode) => (
    <Group direction="vertical" gap={16}>
      <BodyText variant="medium" colour="neutral-two">
        Flights depart Sunday night and arrive on Monday for hotel check-in.
      </BodyText>
      <HeaderActionsRow overflowGutter={20} gap={8}>
        {headerActions}
      </HeaderActionsRow>
    </Group>
  )
  // Render month selection step
  const renderMonthSelection = () => {
    const sortedYears = Object.keys(monthsByYear).map(Number).sort()

    return (
      <VerticalSpacer gap={12}>
        {sortedYears.map((year) => (
          <div key={year}>
            <Heading variant="heading5">{year}</Heading>
            <MonthsGrid>
              {monthsByYear[year].map((month) => {
                const isCheapest = month.price > 0 && month.price === cheapestMonthPrice
                const priceColour = isCheapest ? 'highlight-secondary' : (luxPlusEnabled ? 'lux-plus' : undefined)

                return (
                  <MonthCard
                    key={month.name}
                    onClick={() => handleMonthSelect(month.name, `${moment(month.monthLabel, 'MMM').format('MMMM')} ${year}`)}
                    className={cn({
                      selected: selectedMonth === month.name,
                      cheapest: isCheapest,
                    })}
                  >
                    <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                      <Heading variant="heading6">{month.monthLabel}</Heading>
                      {isCheapest && (
                        <CheapestBadge>
                          <Caption variant="small" colour="neutral-eight">Best</Caption>
                        </CheapestBadge>
                      )}
                    </Group>
                    <VerticalSpacer gap={2}>
                      {month.fetching && <Caption variant="small">Loading...</Caption>}
                      {!month.fetching && month.price > 0 && (
                        <BodyText variant="large" weight="bold" colour={priceColour}>
                          <Caption variant="large" colour="neutral-three">From</Caption>
                          <FormatCurrency value={month.price} format="roundedDollar" />
                        </BodyText>
                      )}
                      {!month.fetching && !month.price && <Caption variant="small">Unavailable</Caption>}
                    </VerticalSpacer>
                  </MonthCard>
                )
              })}
            </MonthsGrid>
          </div>
        ))}
      </VerticalSpacer>
    )
  }

  // Render date selection step - shows all months grouped by month/year
  const renderDateSelection = () => {
    if (allMonthOptions.length === 0) {
      return (
        <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
          <Heading variant="heading5" align="center">
            No departures available
          </Heading>
          <BodyText variant="medium" align="center" colour="neutral-three">
            Try selecting a different month
          </BodyText>
        </EmptyStateGroup>
      )
    }

    return (
      <VerticalSpacer gap={24}>
        {Object.entries(optionsByMonth).map(([monthKey, options]) => {
          const [month, year] = monthKey.split(' ')

          return (
            <MonthSection
              key={monthKey}
              gap={0}
              ref={(el) => {
                if (monthKey) {
                  monthSectionRefs.current[monthKey] = el
                }
              }}
            >
              <MonthHeaderGroup
                direction="horizontal"
                horizontalAlign="space-between"
                verticalAlign="center"
              >
                <Heading variant="heading4">{month}</Heading>
                <ClickableYearHeading variant="heading4" onClick={handleBackToMonthSelect}>
                  {year}
                </ClickableYearHeading>
              </MonthHeaderGroup>
              <DateCardContainer gap={8}>
                {options.map((dateItem) => {
                  const commonProps = {
                    key: dateItem.id,
                    checkIn: dateItem.checkIn,
                    checkOut: dateItem.checkOut,
                    departDate: dateItem.departDate,
                    returnDate: dateItem.returnDate,
                    nights: nights,
                    priceMode: priceMode,
                    luxPlusEnabled: luxPlusEnabled,
                    isSelected: dateItem.id === selectedOptionId,
                    onSelect: onDateSelect,
                  }

                  switch (mode) {
                    case 'search':
                      return (
                        <MaldivesDirectSearchFlightCalendarItemMobile
                          {...commonProps}
                          rooms={rooms}
                          fareClass={fareClass}
                        />
                      )
                    case 'offer': {
                      // Get pre-calculated bundle data for this date
                      const itemData = itemBundleDataByDate?.[dateItem.checkIn]

                      // Fallback to default empty state if data is missing
                      const itemBundleData: CalendarItemBundleData = itemData ?? {
                        fetching: true,
                        available: false,
                        totalPrice: 0,
                        pricePerPerson: 0,
                        offerCount: 0,
                        seatsLeft: 0,
                        hasNoPackage: false,
                      }

                      return (
                        <MaldivesOfferCalendarItemMobile
                          {...commonProps}
                          bundleData={itemBundleData}
                        />
                      )
                    }
                    default:
                      return null
                  }
                })}
              </DateCardContainer>
            </MonthSection>
          )
        })}
      </VerticalSpacer>
    )
  }

  return (
    <>
      {renderHeader(headerActions)}
      <div style={{ display: currentStep === SelectionStep.MONTH ? 'block' : 'none' }}>
        {renderMonthSelection()}
      </div>
      <div style={{ display: currentStep === SelectionStep.DATE ? 'block' : 'none' }}>
        {renderDateSelection()}
      </div>
    </>
  )
}

export default MaldivesDirectSearchFlightCalendarMobile
